import { DEFAULT_IMG } from '@/util/constants'

const FIREBASE_STORAGE_URL = 'firebasestorage.googleapis.com'

// Enhanced image optimization with multiple quality levels
export const urlParser = (url, options = {}) => {
  if (!url) return DEFAULT_IMG;
  
  // Check if this is a Firebase Storage URL
  if (url.includes(FIREBASE_STORAGE_URL)) {
    // Firebase Storage URLs are already optimized and don't need additional parameters
    // Just return the URL as is, but clean up any token parameters
    return url.replace(/&token=[^&]+/, "");
  }
  
  // Handle URLs that might already have protocol
  let cleanUrl = url;
  if (url.startsWith('//')) {
    cleanUrl = 'https:' + url;
  } else if (!url.startsWith('http')) {
    cleanUrl = 'https://' + url;
  }
  
  if (cleanUrl && cleanUrl.includes("downloads")) {
    cleanUrl = cleanUrl.replace("downloads", "images");
  }

  const {
    width = width || 800,
    height,
    quality = 80,
    format = 'jpg',
    fit = 'fill'
  } = options;

  let optimizedUrl = cleanUrl + '?';
  
  // Add width
  optimizedUrl += `w=${width}`;
  
  // Add height if specified
  if (height) {
    optimizedUrl += `&h=${height}`;
  }
  
  // Add fit mode
  optimizedUrl += `&fit=${fit}`;
  
  // Add format (always convert to jpg for better compression)
  optimizedUrl += `&fm=${format}`;
  
  // Add quality
  optimizedUrl += `&q=${quality}`;

  return optimizedUrl;
};

// Optimized version for different use cases
export const getOptimizedImageUrl = (url, useCase = 'default') => {
  const optimizationConfigs = {
    thumbnail: { width: 400, height: 300, quality: 70 },
    card: { width: 600, quality: 75 },
    default: { width: 800, quality: 80 },
    large: { width: 1200, quality: 85 },
    hero: { width: 1600, quality: 90 }
  };

  return urlParser(url, optimizationConfigs[useCase] || optimizationConfigs.default);
};

// Responsive image URLs for different screen sizes
export const getResponsiveImageUrls = (url) => {
  if (!url) return {};
  
  // For Firebase Storage URLs, return the same URL for all sizes since they're already optimized
  if (url.includes(FIREBASE_STORAGE_URL)) {
    const cleanUrl = url.replace(/&token=[^&]+/, "");
    return {
      mobile: cleanUrl,
      tablet: cleanUrl,
      desktop: cleanUrl,
      large: cleanUrl
    };
  }
  
  return {
    mobile: urlParser(url, { width: 400, quality: 70 }),
    tablet: urlParser(url, { width: 600, quality: 75 }),
    desktop: urlParser(url, { width: 800, quality: 80 }),
    large: urlParser(url, { width: 1200, quality: 85 })
  };
};

export const addSizeToUrl = (coverImg, coverImgTitle) => {
  if (!coverImg) return DEFAULT_IMG;
  if (!coverImgTitle) return coverImg;

  const parsedUrl = coverImg.replace(/&token=[^&]+/, "");
  const resizedFileName = coverImgTitle.replace(/(\.[^.]+)$/, "_800x1200$1");
  const isFirebaseUrl = coverImg.includes(FIREBASE_STORAGE_URL);
  
  if (isFirebaseUrl) {
    const encodedOriginal = encodeURIComponent(coverImgTitle);
    const encodedResized = encodeURIComponent(resizedFileName);
    
    if (parsedUrl.includes(encodedOriginal)) {
      return parsedUrl.replace(encodedOriginal, encodedResized);
    }
    return parsedUrl.replace(coverImgTitle, resizedFileName);
  }
  
  return parsedUrl.replace(coverImgTitle, resizedFileName);
};
